import React, { useState } from 'react';
import { Box, Sphere, Cylinder, Cone, Torus } from '@react-three/drei';
import { Color } from 'three';
import { SceneObject } from '../../../types';

interface PrimitiveObjectProps {
  object: SceneObject;
  isSelected: boolean;
  deviceData?: any;
  visualizationMode: string;
  showDataLabels: boolean;
  showHeatmap: boolean;
}

export const PrimitiveObject: React.FC<PrimitiveObjectProps> = ({ object, isSelected }) => {
  const [hovered, setHovered] = useState(false);

  // Get primitive-specific properties
  const primitiveType = object.metadata?.primitiveType || 'box';
  const primitiveColor = object.metadata?.color || '#888888';

  // Color based on state
  const finalColor = new Color(isSelected ? '#00ff88' : hovered ? '#88ff88' : primitiveColor);

  // Primitive geometry based on type
  const renderPrimitiveGeometry = () => {
    const scale = object.scale || [1, 1, 1];

    switch (primitiveType) {
      case 'box':
        return (
          <Box args={[1, 1, 1]} scale={scale}>
            <meshPhongMaterial color={finalColor} />
          </Box>
        );

      case 'sphere':
        return (
          <Sphere args={[0.5, 32, 32]} scale={scale}>
            <meshPhongMaterial color={finalColor} />
          </Sphere>
        );

      case 'cylinder':
        return (
          <Cylinder args={[0.5, 0.5, 1, 32]} scale={scale}>
            <meshPhongMaterial color={finalColor} />
          </Cylinder>
        );

      case 'cone':
        return (
          <Cone args={[0.5, 1, 32]} scale={scale}>
            <meshPhongMaterial color={finalColor} />
          </Cone>
        );

      case 'torus':
        return (
          <Torus args={[0.5, 0.2, 16, 32]} scale={scale}>
            <meshPhongMaterial color={finalColor} />
          </Torus>
        );

      default:
        return (
          <Box args={[1, 1, 1]} scale={scale}>
            <meshPhongMaterial color={finalColor} />
          </Box>
        );
    }
  };

  return (
    <group
      position={object.position as [number, number, number]}
      rotation={object.rotation as [number, number, number]}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {renderPrimitiveGeometry()}

      {/* Selection outline */}
      {isSelected && (
        <Box args={[1.2, 1.2, 1.2]} scale={object.scale || [1, 1, 1]}>
          <meshBasicMaterial color="#00ff88" opacity={0.3} />
        </Box>
      )}
    </group>
  );
};
